/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
// components
import { Helmet } from 'react-helmet';
import { RouterProvider } from 'react-router-dom';
import IframeHostContext, { initIframeHost } from '../../contexts/iframeHost';
import Loader from '../../../../../ui-kitchen/src/components/Loader';
import MaintenancePage from '../../../../../ui-kitchen/src/components/MaintenancePage';
import Logger from '../../../../../ui-kitchen/src/utils/logger';
// providers
import createWidgetHashRouter from './createWidgetHashRouter';
import AnalyticsFactory from '../../../../../ui-kitchen/src/analytics';
import getEnvConfigs from '../../../scripts/getEnvConfigs';
// slices
import { setLocale, selectLanguageCode } from '../../../../../ui-kitchen/src/reducers/i18nSlice';
import { setSignal } from '../../../../../ui-kitchen/src/reducers/signalSlice';
import {
  initCoreISODateThunk, initCoreSeatsThunk, initCoreSeatsMinMaxThunk, initCoreTimeThunk, setAppContext, setServiceConfig,
} from '../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers/resyConfigSlice';
import containerQueryConstants from '../../../../../ui-kitchen/src/constants/containerQuery';
import { updateMatchMedia } from '../../../../../ui-kitchen/src/reducers/matchMediaSlice';
import { fetchRefreshToken, selectIsAuthenticated, setAuthToken } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import { fetchIpdata } from '../../../../../ui-kitchen/src/reducers/ipdataSlice';
import { getUserThunk, setUserLocation } from '../../../../../ui-kitchen/src/reducers/userSlice';
// utils
import isMaintenancePageEnabled, { fetchMaintenanceConfig } from '../../../../../ui-kitchen/src/utils/isMaintenancePageEnabled';
import { initMediaQueryListeners } from '../../../../../ui-kitchen/src/utils/mediaQueryListeners';
import { getURLSearchParams } from '../../../../../ui-kitchen/src/utils/searchParamUtils';
import { isValidLatLong } from '../../../../../ui-kitchen/src/utils/userSliceUtils';
import loadScriptSupplier from '../../../../../ui-kitchen/src/utils/loadScriptSupplier/loadScriptSupplier';
// api
import { postUserLocation } from '../../../../../ui-kitchen/src/api';
// constants
import APP_CONTEXT from '../../../../../ui-kitchen/src/constants/appContext';

// These checks indicate that init should occur once, which should
// be the case once 100% React and not part of Angular route stack
let initCoreData = false;
let initMediaQueries = false;
let initAuthTokenCheck = false;
let initIpdata = false;
let initLocations = false;

const DEFAULT_CONSENT_MARKET = 'GB';

const WidgetAppContainer = (props) => {
  const {
    fetchIpdata,
    fetchRefreshToken,
    initCoreISODateThunk,
    initCoreSeatsThunk,
    initCoreSeatsMinMaxThunk,
    initCoreTimeThunk,
    ipdata,
    isAuthenticated,
    languageCode,
    loadResyConfigThunk,
    locale,
    setAppContext,
    setAuthToken,
    setLocale,
    setSignal,
    setServiceConfig,
    setUserLocation,
    updateMatchMedia,
  } = props;

  const [hasAuthLoaded, setHasAuthLoaded] = useState(false);
  useEffect(() => {
    const doFetch = async () => {
      await fetchRefreshToken(APP_CONTEXT.WIDGET);
      setHasAuthLoaded(true);
    };
    doFetch();
  }, []);

  // The maintenance schedule is published to S3 rather than served by the API, so
  // it stays readable while the platform it describes is down.
  const [maintenance, setMaintenance] = useState(null);
  useEffect(() => {
    let ignore = false;
    const { Config } = getEnvConfigs();
    fetchMaintenanceConfig(Config.assetsBaseUrl).then((config) => {
      if (!ignore) {
        setMaintenance(config);
      }
    });
    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;
    const doLoadResyConfig = () => loadResyConfigThunk()
      .then(() => {
        if (!ignore) {
          initCoreSeatsMinMaxThunk();
        }
      })
      .catch((e) => {
        Logger.error('** WidgetAppContainer: Error loading Resy config', e);
      });

    setAppContext(APP_CONTEXT.WIDGET);
    setLocale(locale);
    initCoreISODateThunk();
    initCoreSeatsThunk(false);
    initCoreTimeThunk();
    initCoreData = true;
    doLoadResyConfig();
    return () => {
      ignore = true;
    };
  }, []);

  // assessAuthToken refactor
  const searchParams = getURLSearchParams();
  /* istanbul ignore else */
  if (!initAuthTokenCheck) {
    initAuthTokenCheck = true;
    const authToken = searchParams.get('authToken');
    /* istanbul ignore else */
    if (authToken) { setAuthToken(authToken); }
  }
  /* istanbul ignore else */
  if (!initMediaQueries) {
    initMediaQueries = true;
    initMediaQueryListeners(() => updateMatchMedia(), containerQueryConstants);
  }
  /* istanbul ignore else */
  if (!initIpdata) {
    initIpdata = true;
    fetchIpdata();
  }
  // First part of ipdata useEffect that requires Redux context to:
  // 1. set location on Redux userSlice
  // 2. post location to User API
  // 3. set consent market for script supplier
  useEffect(() => {
    const hasScriptSupplierElement = document.getElementById('script-supplier');
    // 1
    /* istanbul ignore else */
    if (ipdata.loaded && !initLocations) {
      initLocations = true;
      setUserLocation(ipdata);
    }
    // 2
    /* istanbul ignore else */
    if (ipdata.loaded && isAuthenticated) {
      /* istanbul ignore else */
      if (isValidLatLong(ipdata)) {
        postUserLocation({
          x: ipdata.long,
          y: ipdata.lat,
        });
      }
    }
    // 3
    /* istanbul ignore else */
    if (ipdata.loaded && !ipdata.loadedFromCookie && !hasScriptSupplierElement) {
      const { countryCode, success } = ipdata;
      const consentMarket = success ? countryCode : DEFAULT_CONSENT_MARKET;
      loadScriptSupplier(consentMarket);
    }
  }, [ipdata.loaded, isAuthenticated]);

  // dispatch serviceConfig values to redux. Refactor to use context in WidgetApp
  const {
    AutomationMode, AnnotationServiceConfig, ResyosServiceConfig, ImageServiceConfig, ItemsServiceConfig, ResyAmexConfig,
  } = getEnvConfigs();

  setServiceConfig({ name: 'automationMode', config: AutomationMode });
  setServiceConfig({ name: 'resyAmexConfig', config: ResyAmexConfig });
  setServiceConfig({ name: 'imageService', config: ImageServiceConfig });
  setServiceConfig({ name: 'itemsService', config: ItemsServiceConfig });
  setServiceConfig({ name: 'annotationService', config: AnnotationServiceConfig });
  setServiceConfig({ name: 'resyosService', config: ResyosServiceConfig });

  const iframeHost = initIframeHost(setSignal);
  // As an Angular service, Analtyics was invoked to return its API object: event, page, etc.
  // Thus this manual invocation is needed here:
  const Analytics = AnalyticsFactory();

  const router = createWidgetHashRouter({
    Analytics, AutomationMode, ResyAmexConfig, preferredLanguage: locale,
  });

  // Ahead of the loading gates below: during a maintenance window the calls those
  // gates wait on are the ones that are down, so they would never let up.
  if (isMaintenancePageEnabled(maintenance)) {
    return <MaintenancePage Analytics={Analytics} maintenance={maintenance} />;
  }

  // These are gates to app loading, and need to confirm this is what we want
  if (!hasAuthLoaded || !initCoreData || !initIpdata) {
    return <Loader />;
  }

  return (
    <IframeHostContext.Provider value={iframeHost}>
      <Helmet>
        <html lang={languageCode} />
      </Helmet>
      <RouterProvider router={router} />
    </IframeHostContext.Provider>
  );
};

WidgetAppContainer.propTypes = {
  fetchRefreshToken: PropTypes.func.isRequired,
  languageCode: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired,
  fetchIpdata: PropTypes.func.isRequired,
  initCoreISODateThunk: PropTypes.func.isRequired,
  initCoreSeatsThunk: PropTypes.func.isRequired,
  initCoreSeatsMinMaxThunk: PropTypes.func.isRequired,
  initCoreTimeThunk: PropTypes.func.isRequired,
  ipdata: PropTypes.shape({
    countryCode: PropTypes.string,
    lat: PropTypes.number,
    loaded: PropTypes.bool,
    loadedFromCookie: PropTypes.bool,
    long: PropTypes.number,
    success: PropTypes.bool,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  loadResyConfigThunk: PropTypes.func.isRequired,
  setAppContext: PropTypes.func.isRequired,
  setAuthToken: PropTypes.func.isRequired,
  setLocale: PropTypes.func.isRequired,
  setSignal: PropTypes.func.isRequired,
  setServiceConfig: PropTypes.func.isRequired,
  setUserLocation: PropTypes.func.isRequired,
  updateMatchMedia: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const { ipdata } = state;
  return {
    ipdata,
    isAuthenticated: selectIsAuthenticated(state),
    languageCode: selectLanguageCode(state),
  };
};

const mapDispatchToProps = {
  fetchIpdata,
  fetchRefreshToken,
  getUserThunk,
  initCoreISODateThunk,
  initCoreSeatsThunk,
  initCoreSeatsMinMaxThunk,
  loadResyConfigThunk,
  initCoreTimeThunk,
  setAppContext,
  setAuthToken,
  setLocale,
  setSignal,
  setServiceConfig,
  setUserLocation,
  updateMatchMedia,
};

export { WidgetAppContainer as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(WidgetAppContainer);
